import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Calendar, MapPin, ArrowLeft, Mail, Phone, Globe, ExternalLink, DollarSign, Users, Ticket, Edit3, Trash2, Tag, Share2, Flag } from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

/** /events/:id — Single event detail with flyer + signup CTAs. */
export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/events/${id}`, {
          headers: user ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {},
        });
        if (res.ok) setEvent(await res.json());
        else if (res.status === 404) navigate('/events');
      } finally { setLoading(false); }
    })();
  }, [id, user, navigate]);

  const remove = async () => {
    if (!confirm('Delete this event?')) return;
    const res = await fetch(`${API}/events/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (res.ok) { toast.success('Event deleted'); navigate('/events'); }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: event.title, url }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success('Link copied'); } catch {}
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0B0D2B] text-white flex items-center justify-center"><Loader2 className="animate-spin text-fuchsia-400" /></div>;
  if (!event) return null;
  const loc = event.location || {};

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-12" data-testid="event-detail-page">
        <button onClick={() => navigate('/events')} className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm mb-5">
          <ArrowLeft size={14} /> All events
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
          {/* Flyer */}
          <div className="rounded-3xl overflow-hidden bg-black aspect-[4/5] sticky top-20 self-start max-h-[45vh] lg:max-h-[80vh]">
            <img src={event.flyer_url} alt={event.title} className="w-full h-full object-contain" />
          </div>

          {/* Details */}
          <article>
            {event.open_to_vendors && (
              <span className="inline-block px-2.5 py-1 rounded-full bg-yellow-400 text-black text-[10px] font-bold uppercase tracking-wider mb-3">
                Vending Opportunity
              </span>
            )}
            <h1 className="text-2xl sm:text-4xl font-bold font-syne leading-tight mb-3">{event.title}</h1>

            <div className="flex flex-wrap items-center gap-4 text-zinc-300 text-sm mb-5">
              <span className="flex items-center gap-1.5"><Calendar size={14} className="text-fuchsia-300" />{fmtDate(event.date_iso)}{event.end_date_iso ? ` – ${fmtDate(event.end_date_iso)}` : ''}</span>
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-cyan-300" />
                {[loc.venue_name, loc.address, loc.city, loc.state, loc.country].filter(Boolean).join(', ')}
              </span>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
              {event.signup_url && (
                <a href={event.signup_url} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-bold flex items-center gap-1.5 shadow-[0_4px_18px_-4px_rgba(250,204,21,0.5)]" data-testid="event-vendor-signup">
                  Vendor Signup <ExternalLink size={12} />
                </a>
              )}
              {event.consumer_signup_url && (
                <a href={event.consumer_signup_url} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/30 text-sm font-bold flex items-center gap-1.5" data-testid="event-rsvp">
                  Get Tickets <Ticket size={12} />
                </a>
              )}
              <button onClick={share} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-zinc-200 hover:text-white text-sm font-bold flex items-center gap-1.5" data-testid="event-share">
                <Share2 size={12} /> Share
              </button>
              {event.viewer_is_owner && (
                <>
                  <button onClick={() => toast('Edit not implemented in this view yet — message support')} className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white text-xs font-semibold flex items-center gap-1.5" data-testid="event-edit">
                    <Edit3 size={12} /> Edit
                  </button>
                  <button onClick={remove} className="px-3 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 text-xs font-semibold flex items-center gap-1.5" data-testid="event-delete">
                    <Trash2 size={12} /> Delete
                  </button>
                </>
              )}
            </div>

            {/* Vending details */}
            {event.open_to_vendors && (
              <div className="rounded-2xl bg-fuchsia-500/5 border border-fuchsia-500/20 p-4 mb-6 grid grid-cols-3 gap-3">
                {event.vendor_fee !== null && event.vendor_fee !== undefined && (
                  <Stat icon={DollarSign} label="Booth fee" value={`$${event.vendor_fee}`} color="text-yellow-400" />
                )}
                {event.vendor_slots && (
                  <Stat icon={Users} label="Vendor slots" value={event.vendor_slots} color="text-cyan-300" />
                )}
                {event.consumer_ticket_price !== null && event.consumer_ticket_price !== undefined && (
                  <Stat icon={Ticket} label="Ticket" value={event.consumer_ticket_price ? `$${event.consumer_ticket_price}` : 'Free'} color="text-emerald-300" />
                )}
              </div>
            )}

            {event.description && (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mb-6">
                <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-2">About this event</h2>
                <p className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed" data-testid="event-description-display">{event.description}</p>
              </div>
            )}

            {/* Contact */}
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5 mb-6 space-y-2">
              <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-2">Organizer</h2>
              <div className="flex items-center gap-3">
                {event.host?.profile_image ? (
                  <img src={event.host.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-fuchsia-500/30 flex items-center justify-center font-bold text-white">{(event.host?.name || '?')[0]}</div>
                )}
                <div>
                  <p className="text-white font-semibold text-sm">{event.host?.name}</p>
                  <p className="text-zinc-500 text-[11px] uppercase tracking-wider">{event.host_role}</p>
                </div>
              </div>
              {event.contact_email && <ContactRow icon={Mail} label="Email" value={event.contact_email} href={`mailto:${event.contact_email}`} />}
              {event.contact_phone && <ContactRow icon={Phone} label="Phone" value={event.contact_phone} href={`tel:${event.contact_phone}`} />}
              {event.organizer_website && <ContactRow icon={Globe} label="Website" value={event.organizer_website} href={event.organizer_website} />}
            </div>

            {event.tags && event.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {event.tags.map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-xs flex items-center gap-1">
                    <Tag size={10} />{t}
                  </span>
                ))}
              </div>
            )}

            {user && !event.viewer_is_owner && (
              <button
                onClick={async () => {
                  const reason = prompt('Report this event — reason (e.g. duplicate, inappropriate):', 'duplicate');
                  if (!reason) return;
                  await fetch(`${API}/events/${id}/report`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                    body: JSON.stringify({ reason }),
                  });
                  toast.success('Reported. Thanks!');
                }}
                className="text-xs text-zinc-400 hover:text-red-300 flex items-center gap-1.5"
                data-testid="event-report"
              >
                <Flag size={11} /> Report duplicate / issue
              </button>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color = 'text-white' }) => (
  <div className="text-center">
    <div className={`flex items-center justify-center gap-1 ${color} text-[10px] uppercase tracking-wider font-semibold mb-0.5`}>
      <Icon size={10} /> {label}
    </div>
    <div className="text-white text-base font-bold">{value}</div>
  </div>
);

const ContactRow = ({ icon: Icon, label, value, href }) => (
  <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm pt-2">
    <Icon size={13} className="text-fuchsia-300" /> {value}
  </a>
);
